import {
  deleteV1CompaniesCompanyIdPrevailingWageBenefitsId,
  getV1CompaniesCompanyIdPrevailingWageBenefits,
  patchV1CompaniesCompanyIdPrevailingWageBenefitsId,
  postV1CompaniesCompanyIdPrevailingWageBenefits,
  PrevailingWageBenefit_Create,
  PrevailingWageBenefit_Update,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';

import { PrevailingWageBenefit } from './models';

interface FetchBenefitsParams {
  companyId: string;
  cursor?: string;
  limit?: number;
  link?: PaginationLink;
}

interface CreateBenefitParams {
  companyId: string;
  benefit: PrevailingWageBenefit_Create;
}

interface UpdateBenefitParams {
  benefitId: string;
  benefit: PrevailingWageBenefit_Update;
  companyId: string;
}

interface DeleteBenefitParams {
  benefitId: string;
  companyId: string;
}

export default function usePrevailingWageBenefits() {
  const fetchBenefits = useCallback(
    async ({ companyId, link, cursor, limit }: FetchBenefitsParams) => {
      try {
        const response = await getV1CompaniesCompanyIdPrevailingWageBenefits({
          path: { 'company-id': companyId },
          query: {
            'page[limit]': limit || DEFAULT_API_PAGE_SIZE,
            ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
          },
        });
        const benefits = response.data.data.map((item) =>
          PrevailingWageBenefit.parse(item),
        );
        const pagination = extractPagination(response);

        return { data: benefits, pagination };
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.prevailing_wage_benefits.failed_to_fetch'),
        );
      }
    },
    [],
  );

  const createBenefit = useCallback(
    async ({ companyId, benefit }: CreateBenefitParams) => {
      try {
        const response = await postV1CompaniesCompanyIdPrevailingWageBenefits({
          path: { 'company-id': companyId },
          body: benefit,
        });

        return PrevailingWageBenefit.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.prevailing_wage_benefits.failed_to_create'),
        );
        throw error;
      }
    },
    [],
  );

  const updateBenefit = useCallback(
    async ({ companyId, benefit, benefitId }: UpdateBenefitParams) => {
      try {
        const response = await patchV1CompaniesCompanyIdPrevailingWageBenefitsId({
          path: { 'company-id': companyId, id: benefitId },
          body: benefit,
        });

        return PrevailingWageBenefit.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.prevailing_wage_benefits.failed_to_update'),
        );
        throw error;
      }
    },
    [],
  );

  const deleteBenefit = useCallback(
    async ({ companyId, benefitId }: DeleteBenefitParams) => {
      try {
        await deleteV1CompaniesCompanyIdPrevailingWageBenefitsId({
          path: { 'company-id': companyId, id: benefitId },
        });

        return true;
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.prevailing_wage_benefits.failed_to_delete'),
        );
      }
    },
    [],
  );

  return { createBenefit, deleteBenefit, fetchBenefits, updateBenefit };
}
